import React, { useState, useEffect } from 'react';
import { WarehouseNode, CreateNodeRequest, UpdateNodeRequest } from '../../types';
import './NodeManagement.css';

interface NodeFormProps {
  node?: WarehouseNode; // If provided, we're editing; otherwise, creating
  onSubmit: (nodeData: CreateNodeRequest | UpdateNodeRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

interface FormData {
  name: string;
  location: string;
  capacity: string;
  active: boolean;
}

interface FormErrors {
  name?: string;
  location?: string;
  capacity?: string;
}

const NodeForm: React.FC<NodeFormProps> = ({ 
  node, 
  onSubmit, 
  onCancel, 
  loading = false 
}) => {
  const isEditing = !!node;
  
  const [formData, setFormData] = useState<FormData>({
    name: node?.name || '',
    location: node?.location || '',
    capacity: node?.capacity?.toString() || '',
    active: node?.active ?? true,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update form data when node prop changes
  useEffect(() => {
    if (node) {
      setFormData({
        name: node.name,
        location: node.location,
        capacity: node.capacity?.toString() || '',
        active: node.active,
      });
    }
  }, [node]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Node name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Node name must be at least 2 characters';
    } else if (formData.name.trim().length > 50) {
      newErrors.name = 'Node name must be less than 50 characters';
    }

    // Validate location (optional but if provided, should be reasonable)
    if (formData.location.trim().length > 100) {
      newErrors.location = 'Location must be less than 100 characters';
    }

    // Validate capacity (optional but if provided, should be positive)
    if (formData.capacity.trim()) {
      const capacityNum = parseFloat(formData.capacity);
      if (isNaN(capacityNum) || capacityNum <= 0) {
        newErrors.capacity = 'Capacity must be a positive number';
      } else if (capacityNum > 1000000000) {
        newErrors.capacity = 'Capacity must be less than 1 billion';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData: CreateNodeRequest | UpdateNodeRequest = {
        name: formData.name.trim(),
        location: formData.location.trim(),
        active: formData.active,
      };

      // Only include capacity if it's provided
      if (formData.capacity.trim()) {
        submitData.capacity = parseFloat(formData.capacity);
      }

      await onSubmit(submitData);
      
      // Reset form if creating a new node
      if (!isEditing) {
        setFormData({
          name: '',
          location: '',
          capacity: '',
          active: true,
        });
      }
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="node-form-container">
      <div className="node-form-header">
        <h3>{isEditing ? 'Edit Warehouse Node' : 'Add New Warehouse Node'}</h3>
      </div>

      <form onSubmit={handleSubmit} className="node-form">
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            Node Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`form-input ${errors.name ? 'error' : ''}`}
            placeholder="Enter warehouse node name"
            disabled={isSubmitting || loading}
            maxLength={50}
          />
          {errors.name && <div className="error-message">{errors.name}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="location" className="form-label">
            Location
          </label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            className={`form-input ${errors.location ? 'error' : ''}`}
            placeholder="Enter location (e.g., New York, NY)"
            disabled={isSubmitting || loading}
            maxLength={100}
          />
          {errors.location && <div className="error-message">{errors.location}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="capacity" className="form-label">
            Capacity (units)
          </label>
          <input
            type="number"
            id="capacity"
            name="capacity"
            value={formData.capacity}
            onChange={handleInputChange}
            className={`form-input ${errors.capacity ? 'error' : ''}`}
            placeholder="Enter maximum capacity (optional)"
            disabled={isSubmitting || loading}
            min="1"
            step="1"
          />
          {errors.capacity && <div className="error-message">{errors.capacity}</div>}
          <div className="form-help">Leave empty for unlimited capacity</div>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleInputChange}
              disabled={isSubmitting || loading}
            />
            <span className="checkbox-text">Active node</span>
          </label>
          <div className="form-help">Inactive nodes are excluded from optimization</div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={isSubmitting || loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || loading}
          >
            {isSubmitting ? 'Saving...' : (isEditing ? 'Update Node' : 'Create Node')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NodeForm;